import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, retry, timeout } from 'rxjs/operators';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';

import { selectedAddressModel } from '../models/selectedAddress.interface';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  constructor(
    private http: HttpClient
  ) { }

  /**
   * @description Check user is logged in or not
   * @returns boolean
   */
  isLoggedIn(): boolean {
    return !!localStorage.getItem(Resources.SessionStorageKeys.IsLoggedIn);
  }

  /**
   * @param key string
   * @param value any
   * @description Store date into client session
   */
  setItemIntoSession(key: string, value: any): void {
    // if (!key || !value) {
    if (!key) {
      throw new Error('Key and Value are mandatory fields and neither be empty nor blank.');
    }

    // Store into client session
    sessionStorage.setItem(key, (typeof value == 'string') ? value : JSON.stringify(value));
  }

  /**
   * 
   * @param key string
   * @description Get data from client session
   */
  getItemFromSession(key: string): any {
    if (!!sessionStorage.getItem(key)) {
      // Get from client session
      try {
        return JSON.parse(sessionStorage.getItem(key));
      } catch (e) {
        return sessionStorage.getItem(key)
      }
    }
    // else {
    //   throw Error(`Data is not available for respective key.`);
    // }
  }

  /**
   * 
   * @param key string
   * @description Remove data from respective key
   */
  removeItemFromSession(key: string): void {
    // Remove from client session
    if (!!sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
    }
    //  else {
    //   throw Error(`Key is not available.`);
    // }
  }

  /**
   * @param key string
   * @param value any
   * @description Store date into client local storage
   */
  setItemIntoLocal(key: string, value: any): void {
    // if (!key || !value) {
    if (!key) {
      throw new Error('Key and Value are mandatory fields and neither be empty nor blank.');
    }

    // Store into client local storage
    localStorage.setItem(key, (typeof value == 'string') ? value : JSON.stringify(value));
  }

  /**
   * 
   * @param key string
   * @description Get data from client local storage
   */
  getItemFromLocal(key: string): any {
    if (!!localStorage.getItem(key)) {
      // Get from client local storage
      try {
        return JSON.parse(localStorage.getItem(key));
      } catch (e) {
        return localStorage.getItem(key)
      }
    }
    // else {
    //   throw Error(`Data is not available for respective key.`);
    // }
  }

  /**
   * 
   * @param key string
   * @description Remove data from respective key
   */
  removeItemFromLocal(key: string): void {
    // Remove from client local storage
    if (!!localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
  }

  /**
   * @description Clear client session storage
   */
  clearSessionStorage(): void {
    sessionStorage.clear();
  }

  /**
   * @description Clear client local storage
   */
  clearLocalStorage(): void {
    localStorage.clear();
  }

  /**
   * @description This method is used to restore session data from the server using correlationId. It fetches the session data and stores it in the session storage.
   * @param correlationId string
   * @returns Observable<any>
   * @throws Error if correlationId is not provided or if the session data cannot be fetched
   * @example this.sessionService.restoreSessionData('12345').subscribe()
   */
  restoreSessionData(correlationId: string, email?: string): any {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'e_api_key': environment.resource_identifier
      })
    };
    return this.http.post<any>(`${environment?.api_url}/v1/users/sessionData`, {
      correlationId: correlationId,
      email: email || this.getItemFromLocal(Resources.SessionStorageKeys.Email),
    }, httpOptions).pipe(
      timeout(30000),
      retry(1),
      map((data: any) => {
        if (!email) {
          if (data) {
            // User consent and email
            this.setItemIntoLocal(Resources.SessionStorageKeys.IsConsentApproval, true);
            this.setItemIntoLocal(Resources.SessionStorageKeys.Email, data.email);

            // Selected address
            const selectedAddress: selectedAddressModel = {
              addressId: data?.addressId,
              addressPostcode: data?.postCode,
              addressText: data?.addressLine,
              gbgVersion: data?.gbgVersion || 'V2',
              dnoId: data?.dnoId || data?.dnoRegion
            };

            if (data?.mpan) {
              selectedAddress.mpan = data?.mpan;
            }

            if (data?.mprn) {
              selectedAddress.mprn = data?.mprn;
            }

            this.setItemIntoSession(Resources.SessionStorageKeys.SelectedAddress, selectedAddress);
            this.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, data?.postCode);

            // Set current usage data
            this.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, correlationId);
            this.setItemIntoSession(Resources.SessionStorageKeys.DnoId, selectedAddress?.dnoId || data?.dnoRegion || '');
            this.setItemIntoSession(Resources.SessionStorageKeys.CurrentUsage, data);

            // Others important session data
            this.setItemIntoSession(Resources.SessionStorageKeys.Frequency, 'Monthly');
            this.setItemIntoSession(Resources.SessionStorageKeys.IsAddressBlocked, false);

            // Smart meter flags
            this.setItemIntoSession(Resources.SessionStorageKeys.IsNotNow, true);

          } else {
            console.warn('No session data found for the provided correlationId.');
          }
        }
      })
    )
  }
}
